import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Copy, Plus, Trash2 } from "lucide-react";
import { Link } from "@/components/ui/link";
import { useParams } from "@/lib/navigation";
import * as React from "react";

import { LiveVersionCard } from "@/components/authoring/live-version";
import { SemanticCacheCard } from "@/components/authoring/semantic-cache-card";
import { VersionDialog } from "@/components/authoring/version-dialog";
import { PageHeader } from "@/components/layout-bits";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Badge,
  Button,
  Card,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import { deleteVersion, publishVersion, unpublishVersion } from "@/lib/api/endpoints";
import { useAgent, useVersions } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import type { VersionView } from "@/types/api";

const STATUS_VARIANT: Record<string, "success" | "warning" | "secondary"> = {
  active: "success",
  commissioned: "success",
  draft: "secondary",
  evaluated: "warning",
  suspended: "warning",
  retired: "secondary",
};

export default function AgentDetailPage() {
  const slug = String(useParams().slug);
  const qc = useQueryClient();
  const agent = useAgent(slug);
  const versions = useVersions(slug);

  const [createOpen, setCreateOpen] = React.useState(false);
  const [cloneFrom, setCloneFrom] = React.useState<string | undefined>();
  const [toDelete, setToDelete] = React.useState<VersionView | undefined>();

  const del = useMutation({
    mutationFn: (label: string) => deleteVersion(slug, label),
    onSuccess: async () => {
      toast.success("Version deleted.");
      await qc.invalidateQueries({ queryKey: qk.versions(slug) });
      setToDelete(undefined);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // One published version per agent — the one users chat with in the store.
  const publish = useMutation({
    mutationFn: (v: VersionView) =>
      v.published ? unpublishVersion(slug, v.version) : publishVersion(slug, v.version),
    onSuccess: async (v) => {
      toast.success(
        v.published ? `${v.version} is now live in the agent store.` : "Removed from the store.",
      );
      await qc.invalidateQueries({ queryKey: qk.versions(slug) });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div>
      <Link
        href="/agents"
        className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Agents
      </Link>
      <PageHeader
        title={agent.data?.name ?? slug}
        description={agent.data ? `Domain: ${agent.data.domain_name}` : "Agent versions"}
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setCloneFrom(undefined);
                setCreateOpen(true);
              }}
            >
              <Plus className="h-4 w-4" />
              New version
            </Button>
          </div>
        }
      />

      <LiveVersionCard agentSlug={slug} />
      <SemanticCacheCard agentSlug={slug} />

      {versions.isError ? (
        <ErrorState error={versions.error} onRetry={() => versions.refetch()} />
      ) : versions.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : (versions.data ?? []).length === 0 ? (
        <EmptyState
          title="No versions yet"
          hint="Create a version to define this agent's soul, instructions, and skills."
        />
      ) : (
        <Card className="p-4">
          <Table>
            <THead>
              <TR>
                <TH>Version</TH>
                <TH>Tag</TH>
                <TH>Status</TH>
                <TH>Chat</TH>
                <TH>Store</TH>
                <TH>Skills</TH>
                <TH>Tools</TH>
                <TH className="w-40 text-right">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {(versions.data ?? []).map((v) => (
                <TR key={v.id}>
                  <TD className="font-mono text-xs">
                    <Link
                      href={`/agents/${slug}/versions/${v.version}`}
                      className="font-medium hover:underline"
                    >
                      {v.version}
                    </Link>
                  </TD>
                  <TD>
                    {v.tag ? (
                      <Badge variant="secondary">{v.tag}</Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TD>
                  <TD>
                    <Badge variant={STATUS_VARIANT[v.status] ?? "secondary"}>{v.status}</Badge>
                  </TD>
                  <TD className="text-muted-foreground">{v.supports_chat ? "Yes" : "No"}</TD>
                  <TD>
                    {v.published ? (
                      <Badge variant="success">Published</Badge>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TD>
                  <TD>{v.skill_count}</TD>
                  <TD>{v.capability_count}</TD>
                  <TD className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button asChild variant="ghost" size="sm">
                        <Link href={`/agents/${slug}/versions/${v.version}`}>Open</Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={publish.isPending || (!v.published && !v.supports_chat)}
                        title={
                          !v.supports_chat ? "Enable chat on this version to publish it" : undefined
                        }
                        onClick={() => publish.mutate(v)}
                      >
                        {v.published ? "Unpublish" : "Publish"}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Clone"
                        onClick={() => {
                          setCloneFrom(v.version);
                          setCreateOpen(true);
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Delete"
                        onClick={() => setToDelete(v)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}

      <VersionDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        agentSlug={slug}
        cloneFrom={cloneFrom}
      />
      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(o) => !o && setToDelete(undefined)}
        title={`Delete version ${toDelete?.version}?`}
        description="This deletes the version and its skills. This cannot be undone."
        pending={del.isPending}
        onConfirm={() => toDelete && del.mutate(toDelete.version)}
      />
    </div>
  );
}
